/**
 * preempt1 b — 가칭 → 정식 단지명 자동 전환의 «결정» 층(순수 · DB·네트워크·LLM 없음).
 *
 * ── 무엇을 하나 ──────────────────────────────────────────────────────────────
 * 분양예정·계획 현장 중 이름이 「~번지 공동주택」「B14BL 공동주택」「~ 신축공사」 꼴(가칭)인 곳에 대해,
 * 같은 주소(지번)·블록을 말하는 정식 단지명 후보(청약홈 공고명·모집공고 PDF 원문·시공사 분양 페이지 크롤 ·
 * CV-N 뉴스/문서 원장)를 모아 «주소/블록 일치» 와 «시공사 일치» 가 «둘 다» 맞을 때만 채택한다(오탐 방지).
 *
 * ── 무엇을 바꾸나(쓰기는 provisional-rename-step 이 한다) ─────────────────────
 *   · display_name = 「{정식명} — {가칭}」(cvn composeDisplayName 규격) → title·h1·meta 가 정식명(cl-core resolveDisplayName 브랜드축).
 *   · slug = 정식명 slug(새 레코드 규칙 generateAptSlugStrict · 가칭 꼬리 제거) · 옛 slug → apt_site_merges(정적 301 맵 원장).
 *   · 본문 요약에 「(가칭 ○○)」 한 줄(provisionalNoteLine — 이 파일 · cl-view 가 읽는다).
 *   · 별칭 표 apt_name_alias_auto 에 정식명·가칭 두 줄(수동 표 apt_name_alias_manual 은 건드리지 않는다).
 * ⛔ name 은 바꾸지 않는다 — 공공API(청약홈·인허가·실거래) 매칭 키다(cvn decide.ts composeDisplayName 머리말과 같은 원칙).
 * ⛔ rank_lock 현장은 아무것도 바꾸지 않는다(보고만).
 */
import { norm } from '@/lib/cvn/decide';
import { generateAptSlugStrict } from '@/lib/apt-slug';

/* ───────────────────────────── 가칭 판정 ───────────────────────────── */

/** 가칭 이름 꼴 — 지번(번지·「동 123-4」)·블록(BL·블록)·「공동주택」·「신축공사」·「일원」·「부지」·「(가칭)」. */
const PROVISIONAL_RE = /(번지|공동주택|(?:^|[^A-Za-z])[A-Za-z]{0,2}-?\d{1,3}(?:-\d)?\s*(?:BL|bl|블록)|신축공사|일원(?!동)|부지|[（(]\s*(?:가칭|가제|임시명|잠정)\s*[）)])/;
/** 지번만 있는 꼴(「거제동 129-5 주거복합」처럼 번지 낱말 없이). */
const BARE_LOT_RE = /[가-힣]+(?:동|리|가)\s*\(?\s*\d{1,5}-\d{1,5}/;

export function isProvisionalSiteName(name: string | null | undefined): boolean {
  const s = String(name ?? '').trim();
  if (!s) return false;
  return PROVISIONAL_RE.test(s) || BARE_LOT_RE.test(s);
}

/** 대상 단계 — 활성 분양예정·계획(394곳 실측 정의: site_planning · pre_announcement · plan_approved). */
export const PROVISIONAL_STAGES = ['site_planning', 'pre_announcement', 'plan_approved'] as const;

/* ───────────────────────────── 근거 추출 ───────────────────────────── */

/** 지번 토큰 「{동·리·가}:{본번[-부번]}」 집합. 「좌동1360번지」「모라동(270-3번지 일원)」「선지리 452-1」 모두. */
export function extractLots(...texts: Array<string | null | undefined>): Set<string> {
  const out = new Set<string>();
  const re = /([가-힣]{1,10}(?:동|리|가))\s*[(（]?\s*(?:산\s*)?(\d{1,5}(?:-\d{1,5})?)(?![\d-])/g;
  for (const t of texts) {
    const s = String(t ?? '');
    let m: RegExpExecArray | null;
    while ((m = re.exec(s))) {
      const dong = m[1].replace(/^(?:특별시|광역시|시|군|구|읍|면)/, '');
      if (dong.length < 2) continue;
      out.add(`${dong}:${m[2]}`);
    }
  }
  return out;
}

/** 블록 토큰(「A-5BL」→A5 · 「B14BL」→B14 · 「공동6BL」→6 · 「A-1블록」→A1). 앞이 영숫자면 블록 낱말이 아니다(16BL ≠ 6). */
export function extractBlocks(...texts: Array<string | null | undefined>): Set<string> {
  const out = new Set<string>();
  const re = /(?<![A-Za-z0-9])([A-Za-z]{0,2})-?(\d{1,3})(?:-(\d{1,2}))?\s*(?:BL|bl|Bl|블록)/g;
  for (const t of texts) {
    const s = String(t ?? '');
    let m: RegExpExecArray | null;
    while ((m = re.exec(s))) out.add(`${m[1].toUpperCase()}${m[2]}${m[3] ? `-${m[3]}` : ''}`);
  }
  return out;
}

const GENERIC_WORDS = new Set([
  '공동주택', '공공분양', '공공주택', '신혼희망타운', '아파트', '주상복합', '주거복합', '주거복합단지', '신축공사', '일원', '번지', '블록',
  '분양', '본청약', '사전청약', '민간', '공공', '지구', '도시개발사업지구', '공동주택용지', '단지', '재개발', '재건축',
]);

/** 블록 판정용 «지구 낱말» — 지역(시도·시군구)·일반어를 뺀 2자 이상 한글 낱말(접미 지구·도시개발사업지구 제거). */
export function zoneWords(text: string | null | undefined, region?: string | null, sigungu?: string | null): string[] {
  const regionStems = [region, sigungu].map((v) => String(v ?? '').trim()).filter(Boolean)
    .flatMap((v) => [v, v.replace(/(특별자치시|특별자치도|광역시|특별시|시|군|구|도)$/, '')]);
  const out = new Set<string>();
  for (const raw of String(text ?? '').split(/[^가-힣]+/)) {
    let w = raw.replace(/(도시개발사업지구|공공주택지구|택지개발지구|국제신도시|신도시|지구)$/, '');
    for (const r of regionStems) if (r && w.startsWith(r) && w.length > r.length) w = w.slice(r.length);
    if (w.length < 2 || GENERIC_WORDS.has(w) || regionStems.includes(w)) continue;
    if (/(동|리|가)$/.test(w) && w.length <= 3) continue; // 법정동 낱말은 지번 축이 맡는다
    out.add(w);
  }
  return [...out];
}

/** 시공사 비교 키 — 「(주)」「㈜」「주식회사」·공백 제거. 여러 시공사는 쉼표·가운뎃점으로 나눈다. */
export function builderKeys(...vals: Array<string | null | undefined | readonly string[]>): string[] {
  const out = new Set<string>();
  for (const v of vals) {
    const arr = Array.isArray(v) ? v : [v as string | null | undefined];
    for (const x of arr) {
      for (const part of String(x ?? '').split(/[,，·/&]|\s외\s|컨소시엄/)) {
        const k = part.replace(/\(주\)|（주）|㈜|주식회사|\s+/g, '').replace(/[()（）]/g, '').trim();
        if (k.length >= 2 && !/^(미정|미상|null|undefined|-)$/.test(k)) out.add(k);
      }
    }
  }
  return [...out];
}

export function builderMatch(a: readonly string[], b: readonly string[]): boolean {
  return a.some((x) => b.some((y) => x === y || (Math.min(x.length, y.length) >= 3 && (x.includes(y) || y.includes(x)))));
}

/** 공고명 정리 — 회차·청약 국면·공급유형 꼬리를 뗀다(「힐스테이트 거제시그니처(1회차)」→「힐스테이트 거제시그니처」). */
export function cleanOfficialName(raw: string | null | undefined): string {
  return String(raw ?? '')
    .replace(/\s*[（(]\s*(?:\d+\s*회차|\d+\s*차\s*분양|본청약|사전청약|공공분양|민간분양|민영|잔여세대|무순위|취소분|추가모집|임의공급)\s*[）)]/g, ' ')
    .replace(/\s+(?:\d+\s*회차|잔여세대|무순위|취소분|추가모집|임의공급)\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** 정식명 slug — 새 레코드 규칙(strict) + 가칭 꼬리 제거(presale/candidate provisionalSlug 와 같은 규칙). */
export function officialSlug(official: string): string {
  return generateAptSlugStrict(String(official ?? '').replace(/\s*[（(]\s*(가칭|가제|임시명|잠정)\s*[）)]\s*/g, ' ').replace(/\s+/g, ' ').trim());
}

/* ───────────────────────────── 판정 ───────────────────────────── */

export interface ProvisionalSite {
  id: string;
  slug: string;
  name: string;
  display_name?: string | null;
  region?: string | null;
  sigungu?: string | null;
  dong?: string | null;
  address?: string | null;
  builder?: string | null;
  builder_normalized?: readonly string[] | null;
  /** 이 현장에 매칭된 인허가 행의 주소·시공사(현장 쪽 근거 보강 — 원천이 준 값). */
  permitAddresses?: readonly string[];
  permitBuilders?: readonly string[];
}

export type EvidenceSource = 'applyhome_notice' | 'notice_pdf' | 'builder_presale' | 'news_cvn';

export interface NameEvidence {
  source: EvidenceSource;
  /** 원천 행 식별자(공고 id · 크롤 후보 id · CV-N 후보 id). */
  ref: string;
  officialRaw: string;
  /** 주소·블록 근거 문자열(공고 주소·PDF 원문 발췌·크롤 주소). */
  addressText?: string | null;
  builder?: string | null;
  url?: string | null;
  /** 원천이 이미 이 현장에 묶어 둔 행(CV-N site_id · 크롤 matched/seeded_site_id). 묶이지 않은 행은 지번·블록이 맞아야 후보가 된다. */
  bound?: boolean;
  /** 지시가 꼽은 원천(청약홈·PDF·시공사 분양 크롤·뉴스) 밖의 «약한» 출처(분양 대행 위성 사이트·일반 웹 글). 신호는 세되 단독으로는 채택하지 않는다. */
  weak?: boolean;
}

/** presale_candidates.source 의 급 — 위성(대행 마이크로사이트)·일반 웹 글은 약한 출처. 시공사 크롤·브랜드관·뉴스·공식은 강한 출처. */
export function isWeakPresaleSource(source: string | null | undefined): boolean {
  return /^(satellite|web):/i.test(String(source ?? ''));
}

export interface Signals { lot: string | null; block: string | null; builder: string | null }

export interface OfficialCandidate {
  official: string;
  signals: Signals;
  /** 오탐 방지 — (지번 또는 블록) 일치 «그리고» 시공사 일치 «그리고» 강한 출처 1건 이상. */
  accept: boolean;
  /** 신호는 다 맞지만 근거가 전부 약한 출처뿐 — 보고만(세션 A 판단). */
  weakOnly: boolean;
  evidence: Array<{ source: EvidenceSource; ref: string; lot: string | null; block: string | null; builder: string | null; weak?: boolean }>;
}

/** 근거 한 장의 신호. */
/**
 * 지역 가드 — 근거 문자열에 현장 시군구(어간 포함)가 보여야 위치 신호를 센다.
 *   같은 법정동·같은 블록 번호·같은 브랜드 낱말이 다른 시군구에 있다(신정동: 울산 남구 ↔ 서울 양천구 · 「금강펜테리움 6BL」).
 *   현장 시군구를 모르면 가드를 걸지 않는다(원천 주소가 없으면 어차피 지번이 안 맞는다).
 */
export function regionOk(site: Pick<ProvisionalSite, 'sigungu'>, text: string): boolean {
  const g = String(site.sigungu ?? '').trim();
  if (!g) return true;
  const stem = g.replace(/(특별자치시|광역시|특별시|시|군|구)$/, '');
  return text.includes(g) || (stem.length >= 2 && text.includes(stem));
}

export function evidenceSignals(site: ProvisionalSite, ev: NameEvidence): Signals {
  const evText = `${ev.officialRaw ?? ''} ${ev.addressText ?? ''}`;
  const inRegion = regionOk(site, evText);
  const siteLots = extractLots(site.name, site.address, ...(site.permitAddresses ?? []));
  const evLots = inRegion ? extractLots(ev.addressText, ev.officialRaw) : new Set<string>();
  const lot = [...siteLots].find((x) => evLots.has(x)) ?? null;

  let block: string | null = null;
  const siteBlocks = extractBlocks(site.name, site.address, ...(site.permitAddresses ?? []));
  if (siteBlocks.size && inRegion) {
    const evBlocks = extractBlocks(evText);
    const shared = [...siteBlocks].find((x) => evBlocks.has(x)) ?? null;
    if (shared) {
      const zones = zoneWords(site.name, site.region, site.sigungu);
      const z = zones.find((w) => evText.includes(w));
      if (z) block = `${z}:${shared}`;
    }
  }

  const siteB = builderKeys(site.builder, site.builder_normalized ?? [], site.permitBuilders ?? []);
  const evB = builderKeys(ev.builder);
  const builder = siteB.length && evB.length && builderMatch(siteB, evB) ? evB[0] : null;
  return { lot, block, builder };
}

/**
 * 후보 묶기 — 정리한 정식명(norm) 단위로 근거를 합친다(같은 이름을 말한 출처끼리만).
 * ⛔ 정식명이 다시 가칭 꼴이거나(LH 「A17블록 신혼희망타운」) 현장 이름과 같으면 후보가 아니다.
 * ⛔ 「(가칭)」이 붙은 크롤 이름(「부암동 데시앙(가칭)」)은 «정식» 이 아니다 — 후보에서 뺀다.
 */
export function collectCandidates(site: ProvisionalSite, evidence: readonly NameEvidence[]): OfficialCandidate[] {
  const by = new Map<string, OfficialCandidate>();
  for (const ev of evidence) {
    if (/[（(]\s*(가칭|가제|임시명|잠정)\s*[）)]/.test(ev.officialRaw ?? '')) continue;
    const official = cleanOfficialName(ev.officialRaw);
    if (!official || isProvisionalSiteName(official)) continue;
    if (norm(official) === norm(site.name)) continue;
    const key = norm(official);
    const sig = evidenceSignals(site, ev);
    // 시공사만 맞는 «묶이지 않은» 행은 후보가 아니다(같은 시공사의 다른 단지 전부가 후보가 되는 것을 막는다).
    if (!sig.lot && !sig.block && !ev.bound) continue;
    const cur = by.get(key) ?? { official, signals: { lot: null, block: null, builder: null }, accept: false, weakOnly: false, evidence: [] };
    cur.signals = { lot: cur.signals.lot ?? sig.lot, block: cur.signals.block ?? sig.block, builder: cur.signals.builder ?? sig.builder };
    cur.evidence.push({ source: ev.source, ref: ev.ref, ...sig, ...(ev.weak ? { weak: true } : {}) });
    by.set(key, cur);
  }
  for (const c of by.values()) {
    const twoOfTwo = !!(c.signals.lot || c.signals.block) && !!c.signals.builder;
    const strong = c.evidence.some((e) => !e.weak);
    c.accept = twoOfTwo && strong;
    c.weakOnly = twoOfTwo && !strong;
  }
  return [...by.values()].sort((a, b) => Number(b.accept) - Number(a.accept) || b.evidence.length - a.evidence.length);
}

export type RenameDecision =
  | 'no_candidate'        // 정식명 후보 없음
  | 'hold_one_signal'     // 후보는 있으나 (지번/블록)·시공사 중 하나뿐
  | 'hold_weak_source'    // 둘 다 맞지만 근거가 위성·일반 웹 글뿐(지시 원천 밖)
  | 'hold_ambiguous'      // 채택 가능한 정식명이 둘 이상
  | 'hold_rank_lock'      // rank_lock 현장 — 보고만
  | 'hold_conflict_site'  // 정식명이 다른 활성 현장의 이름·표시명 — 병합 큐(이름 바꾸면 쌍둥이)
  | 'hold_display_other'  // 이미 다른 정식명이 표시명에 적용됨(CV-N applied) — 덮지 않는다
  | 'accept';

export type SlugAction =
  | 'switch'
  | 'none_same'           // 이미 정식 slug
  | 'hold_slug_taken'     // 다른 행(비활성 포함)이 그 slug 를 쓴다
  | 'hold_prior_merge'    // apt_site_merges 에 그 slug 가 dead 로 있다 — 이미 판정된 방향(블록 slug canonical 등)
  | 'hold_static_map'     // 배포된 정적 301 맵의 키다 — 바꾸면 middleware 301 ↔ 페이지 308 고리
  | 'hold_empty';

export interface RenameContext {
  rankLocked: boolean;
  /** 정식명 norm → 그 이름을 name/표시명(— 왼쪽)으로 쓰는 «다른» 활성 현장 id. */
  otherSiteByName: (normName: string) => string | null;
  slugTaken: (slug: string) => boolean;
  priorMergeDead: (slug: string) => boolean;
  staticMapKey: (slug: string) => boolean;
}

export interface RenamePlan {
  siteId: string;
  name: string;
  oldSlug: string;
  decision: RenameDecision;
  official: string | null;
  displayFrom: string | null;
  displayTo: string | null;
  displayAction: 'set' | 'already' | 'none';
  newSlug: string | null;
  slugAction: SlugAction | 'none';
  candidates: OfficialCandidate[];
  note?: string;
}

/** 표시명 규격(cvn composeDisplayName 과 같은 꼴) — 「{정식명} — {가칭}」. */
export function composeOfficialDisplay(official: string, siteName: string): string {
  const p = official.trim();
  const s = siteName.trim();
  if (!s || norm(p).includes(norm(s))) return p;
  return `${p} — ${s}`;
}

/** 표시명 「X — Y」의 왼쪽(X). 꼴이 아니면 null. */
export function displayBrandOf(display: string | null | undefined): string | null {
  const m = /^(.+?)\s+[—–]\s+(.+)$/.exec(String(display ?? '').trim());
  return m ? m[1].trim() : null;
}

export function planRename(site: ProvisionalSite, evidence: readonly NameEvidence[], ctx: RenameContext): RenamePlan {
  const candidates = collectCandidates(site, evidence);
  const base: RenamePlan = {
    siteId: site.id, name: site.name, oldSlug: site.slug, decision: 'no_candidate', official: null,
    displayFrom: site.display_name ?? null, displayTo: null, displayAction: 'none', newSlug: null, slugAction: 'none', candidates,
  };
  if (!candidates.length) return base;
  const accepted = candidates.filter((c) => c.accept);
  if (!accepted.length) {
    const weak = candidates.find((c) => c.weakOnly);
    if (weak) return { ...base, decision: 'hold_weak_source', official: weak.official };
    return { ...base, decision: 'hold_one_signal', official: candidates[0].official };
  }
  if (accepted.length > 1) return { ...base, decision: 'hold_ambiguous', official: accepted.map((c) => c.official).join(' | ') };
  const official = accepted[0].official;
  if (ctx.rankLocked) return { ...base, decision: 'hold_rank_lock', official };
  const other = ctx.otherSiteByName(norm(official));
  if (other && other !== site.id) return { ...base, decision: 'hold_conflict_site', official, note: `other_site=${other}` };

  const curBrand = displayBrandOf(site.display_name);
  if (curBrand && norm(curBrand) !== norm(official)) return { ...base, decision: 'hold_display_other', official, note: `display_brand=${curBrand}` };

  const displayTo = composeOfficialDisplay(official, site.name);
  const displayAction: RenamePlan['displayAction'] = (site.display_name ?? '').trim() === displayTo ? 'already' : 'set';

  const newSlug = officialSlug(official);
  let slugAction: SlugAction;
  if (!newSlug) slugAction = 'hold_empty';
  else if (newSlug === site.slug) slugAction = 'none_same';
  else if (ctx.staticMapKey(newSlug)) slugAction = 'hold_static_map';
  else if (ctx.priorMergeDead(newSlug)) slugAction = 'hold_prior_merge';
  else if (ctx.slugTaken(newSlug)) slugAction = 'hold_slug_taken';
  else slugAction = 'switch';

  return { ...base, decision: 'accept', official, displayTo, displayAction, newSlug, slugAction };
}

/* ───────────────────────────── 본문 한 줄 ───────────────────────────── */

/**
 * 본문·요약에 남기는 「(가칭 ○○)」 한 줄 — 표시명이 「{정식명} — {가칭}」 꼴이고 오른쪽이 현장 name 이며
 * 그 name 이 가칭 꼴일 때만. 그 밖은 null(정비 「브랜드 — 구역명」은 가칭이 아니다 — 구역명은 정식 사업명).
 * ⛔ DB 를 부르지 않는다(cl-view 가 이미 읽은 site 행으로 판정).
 */
export function provisionalNoteLine(site: { name?: string | null; display_name?: string | null } | null | undefined): string | null {
  const name = String(site?.name ?? '').trim();
  const m = /^(.+?)\s+[—–]\s+(.+)$/.exec(String(site?.display_name ?? '').trim());
  if (!name || !m) return null;
  if (m[2].trim() !== name || !isProvisionalSiteName(name)) return null;
  if (norm(m[1]) === norm(name)) return null;
  return `(가칭 ${name})`;
}

/**
 * 정식명 — 표시명이 「{정식명} — {가칭 name}」 꼴(provisionalNoteLine 비null)이면 왼쪽, 아니면 null.
 * 다른 레인(의도형 키워드·h2 이름 · pre-c intentName)이 «name 대신» 읽을 공급 함수. name 은 공공API 매칭 키라 가칭 그대로 남는다.
 */
export function officialDisplayName(site: { name?: string | null; display_name?: string | null } | null | undefined): string | null {
  return provisionalNoteLine(site) ? displayBrandOf(site?.display_name) : null;
}

/**
 * 측정 키워드 갈아 끼우기 — 키워드가 가칭(공백 무시)으로 시작하면 그 머리를 정식명으로 바꾼 키워드, 아니면 null.
 *   「판문지구 공동주택 1단지 분양가」 + 가칭 「판문지구 공동주택 1단지」 → 「진주 판문지구 레이크써밋 웰가 분양가」
 */
export function retargetKeyword(keyword: string, provisional: string, official: string): string | null {
  const kw = String(keyword ?? '').trim();
  const chars = Array.from(String(provisional ?? '').replace(/\s+/g, ''));
  if (!kw || chars.length < 2 || !official.trim()) return null;
  const esc = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp('^' + chars.map(esc).join('\\s*'));
  const m = re.exec(kw);
  if (!m) return null;
  return `${official.trim()}${kw.slice(m[0].length)}`.replace(/\s+/g, ' ').trim();
}
